import { connect as netConnect, type Socket } from "node:net";
import { type TLSSocket, connect as tlsConnect } from "node:tls";
import { PersistenceError } from "@kortyx/core/errors";

export type RedisReply =
  | string
  | number
  | null
  | RedisReply[]
  | { type: "error"; message: string };

export type RedisClientOptions = {
  url: string;
  connectTimeoutMs?: number;
};

const encodeBulk = (s: string) => {
  const b = Buffer.from(s, "utf8");
  return Buffer.concat([
    Buffer.from(`$${b.length}\r\n`),
    b,
    Buffer.from("\r\n"),
  ]);
};

const encodeCommand = (cmd: string, args: string[]) => {
  const parts = [cmd, ...args];
  const bufs: Buffer[] = [Buffer.from(`*${parts.length}\r\n`)];
  for (const p of parts) bufs.push(encodeBulk(p));
  return Buffer.concat(bufs);
};

function readLine(buf: Buffer, start: number) {
  const idx = buf.indexOf("\r\n", start);
  if (idx === -1) return null;
  return { line: buf.subarray(start, idx).toString("utf8"), next: idx + 2 };
}

function parseReply(
  buf: Buffer,
  start: number,
): { value: RedisReply; next: number } | null {
  if (start >= buf.length) return null;
  const byte = buf[start];
  if (byte === undefined) return null;
  const prefix = String.fromCharCode(byte);
  if (prefix === "+" || prefix === "-" || prefix === ":") {
    const line = readLine(buf, start + 1);
    if (!line) return null;
    if (prefix === "+") return { value: line.line, next: line.next };
    if (prefix === ":")
      return { value: Number.parseInt(line.line, 10), next: line.next };
    return { value: { type: "error", message: line.line }, next: line.next };
  }

  if (prefix === "$") {
    const line = readLine(buf, start + 1);
    if (!line) return null;
    const len = Number.parseInt(line.line, 10);
    if (len === -1) return { value: null, next: line.next };
    const end = line.next + len;
    if (end + 2 > buf.length) return null;
    const data = buf.subarray(line.next, end).toString("utf8");
    return { value: data, next: end + 2 };
  }

  if (prefix === "*") {
    const line = readLine(buf, start + 1);
    if (!line) return null;
    const count = Number.parseInt(line.line, 10);
    if (count === -1) return { value: null, next: line.next };
    const items: RedisReply[] = [];
    let cursor = line.next;
    for (let i = 0; i < count; i++) {
      const parsed = parseReply(buf, cursor);
      if (!parsed) return null;
      items.push(parsed.value);
      cursor = parsed.next;
    }
    return { value: items, next: cursor };
  }

  return {
    value: { type: "error", message: `Unknown RESP prefix: ${prefix}` },
    next: buf.length,
  };
}

export type RedisClient = {
  close?: () => Promise<void>;
  command: (cmd: string, args?: string[]) => Promise<RedisReply>;
  transaction?: (commands: Array<[string, string[]]>) => Promise<RedisReply[]>;
};

export function createRedisClient(options: RedisClientOptions): RedisClient {
  const resolvedOptions: Required<
    Pick<RedisClientOptions, "connectTimeoutMs">
  > &
    Omit<RedisClientOptions, "connectTimeoutMs"> = {
    connectTimeoutMs: options.connectTimeoutMs ?? 5_000,
    url: options.url,
  };

  let socket: Socket | TLSSocket | null = null;
  let buffer = Buffer.alloc(0);
  const inflight: Array<{
    resolve: (v: RedisReply) => void;
    reject: (e: unknown) => void;
  }> = [];

  let connecting: Promise<void> | null = null;
  let ready = false;
  let dispatch = Promise.resolve();
  const exclusive = <T>(operation: () => Promise<T>): Promise<T> => {
    const result = dispatch.then(operation);
    dispatch = result.then(
      () => {},
      () => {},
    );
    return result;
  };

  const drainReplies = () => {
    while (inflight.length) {
      const parsed = parseReply(buffer, 0);
      if (!parsed) return;
      const waiter = inflight.shift()!;
      buffer = buffer.subarray(parsed.next);
      waiter.resolve(parsed.value);
    }
  };

  const send = (cmd: string, args: string[] = []): Promise<RedisReply> => {
    if (!socket) return Promise.reject(new Error("Redis socket not available"));
    const payload = encodeCommand(cmd, args);
    return new Promise<RedisReply>((resolve, reject) => {
      inflight.push({ resolve, reject });
      socket?.write(payload);
    });
  };

  const ensureConnected = async () => {
    if (ready) return;
    if (connecting) return connecting;

    connecting = (async () => {
      const u = new URL(resolvedOptions.url);
      const isTls = u.protocol === "rediss:";
      const host = u.hostname || "127.0.0.1";
      const port = u.port ? Number(u.port) : isTls ? 6380 : 6379;
      const password = u.password ? decodeURIComponent(u.password) : "";
      const username = u.username ? decodeURIComponent(u.username) : "";
      const db =
        u.pathname && u.pathname !== "/" ? Number(u.pathname.slice(1)) : 0;

      const s = isTls
        ? tlsConnect({ host, port, servername: host })
        : netConnect({ host, port });
      socket = s;

      s.setNoDelay(true);
      s.setKeepAlive(true);

      s.on("data", (chunk) => {
        if (socket !== s) return;
        buffer = Buffer.concat([buffer, chunk]);
        drainReplies();
      });

      s.on("error", (err) => {
        if (socket !== s) return;
        ready = false;
        while (inflight.length) inflight.shift()?.reject(err);
      });

      s.on("close", () => {
        if (socket !== s) return;
        ready = false;
        socket = null;
        buffer = Buffer.alloc(0);
        while (inflight.length)
          inflight.shift()?.reject(new Error("Redis socket closed"));
      });

      await new Promise<void>((resolve, reject) => {
        const timer = setTimeout(
          () => reject(new Error("Redis connect timeout")),
          resolvedOptions.connectTimeoutMs,
        );

        const onConnect = () => {
          clearTimeout(timer);
          resolve();
        };

        // net: "connect", tls: "secureConnect" (TLSSocket can also emit "connect")
        s.once("connect", onConnect);
        (s as any).once?.("secureConnect", onConnect);
        s.once("error", (e) => {
          clearTimeout(timer);
          reject(e);
        });
      });

      if (password) {
        const r = await send(
          "AUTH",
          username ? [username, password] : [password],
        );
        if (typeof r === "object" && r && (r as any).type === "error") {
          throw new PersistenceError(
            `Redis AUTH failed: ${(r as any).message}`,
          );
        }
      }

      if (db && Number.isFinite(db) && db > 0) {
        const r = await send("SELECT", [String(db)]);
        if (typeof r === "object" && r && (r as any).type === "error") {
          throw new PersistenceError(
            `Redis SELECT failed: ${(r as any).message}`,
          );
        }
      }

      ready = true;
    })();

    try {
      await connecting;
    } finally {
      connecting = null;
    }
  };

  return {
    async close() {
      socket?.destroy();
      socket = null;
      buffer = Buffer.alloc(0);
      ready = false;
      while (inflight.length)
        inflight.shift()?.reject(new Error("Redis socket closed"));
    },
    async command(cmd: string, args: string[] = []) {
      // Release dispatch after enqueueing; ordinary commands still pipeline.
      const { reply } = await exclusive(async () => {
        await ensureConnected();
        return { reply: send(cmd, args) };
      });
      return reply;
    },
    async transaction(commands) {
      return exclusive(async () => {
        await ensureConnected();
        const started = await send("MULTI");
        if (started !== "OK")
          throw new PersistenceError("Redis transaction could not start.");
        try {
          const queued = await Promise.all(
            commands.map(([command, args]) => send(command, args)),
          );
          if (queued.some((reply) => reply !== "QUEUED")) {
            await send("DISCARD");
            throw new PersistenceError(
              "Redis transaction command was rejected.",
            );
          }
          const result = await send("EXEC");
          if (!Array.isArray(result) || result.length !== commands.length)
            throw new PersistenceError("Redis transaction did not complete.");
          if (
            result.some(
              (reply) =>
                reply !== null &&
                typeof reply === "object" &&
                !Array.isArray(reply) &&
                reply.type === "error",
            )
          )
            throw new PersistenceError("Redis transaction command failed.");
          return result;
        } catch (error) {
          // Never reuse a connection with an unknown transaction state.
          ready = false;
          socket?.destroy();
          throw error;
        }
      });
    },
  };
}
